import PropTypes from 'prop-types';
import { Alert, Button, Container } from 'react-bootstrap';
import React, { useState, useEffect, useRef } from 'react';
import './PreviewBanner.css';

/**
 * A prominent preview/coming soon banner for features that are work in progress.
 * Can be displayed as a dismissible overlay or persistent top banner.
 */
export default function PreviewBanner({
    title = 'Preview Feature',
    message = 'This feature is currently in development.',
    variant = 'warning',
    icon = '🚧',
    showOverlay = true,
    onDismiss,
}) {
    const [dismissed, setDismissed] = useState(false);
    const dismissBtnRef = useRef(null);

    const handleDismiss = () => {
        setDismissed(true);
        if (onDismiss) onDismiss();
    };

    // Auto-focus the dismiss button when the overlay mounts
    const isOverlayVisible = showOverlay && !dismissed;
    useEffect(() => {
        if (isOverlayVisible && dismissBtnRef.current) {
            dismissBtnRef.current.focus();
        }
    }, [isOverlayVisible]);

    // Overlay mode - shows a modal-like overlay on first view
    if (isOverlayVisible) {
        return (
            <div
                className="preview-overlay"
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <div className="preview-overlay-backdrop" />
                <div className="preview-overlay-content">
                    <div className="preview-overlay-card">
                        <div
                            className="preview-overlay-icon"
                            aria-hidden="true"
                        >
                            {icon}
                        </div>
                        <h2 className="preview-overlay-title">{title}</h2>
                        <p className="preview-overlay-message">{message}</p>
                        <div className="preview-overlay-divider" />
                        <p className="preview-overlay-subtitle">
                            Feel free to explore the interface to see
                            what&apos;s coming!
                        </p>
                        <Button
                            ref={dismissBtnRef}
                            variant="light"
                            size="lg"
                            className="preview-overlay-button"
                            onClick={handleDismiss}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') handleDismiss();
                            }}
                        >
                            Got it, let me explore →
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Persistent banner mode - shows after dismissing overlay or if overlay disabled
    return (
        <div className={`preview-banner preview-banner-${variant}`}>
            <Container>
                <Alert
                    variant={variant}
                    className="preview-banner-alert mb-0 d-flex align-items-center justify-content-center"
                >
                    <span className="preview-banner-icon me-2">{icon}</span>
                    <span className="preview-banner-text">
                        <strong>{title}</strong>
                        <span className="d-none d-md-inline"> — {message}</span>
                    </span>
                </Alert>
            </Container>
        </div>
    );
}

PreviewBanner.propTypes = {
    title: PropTypes.string,
    message: PropTypes.string,
    variant: PropTypes.oneOf(['warning', 'info', 'primary', 'secondary']),
    icon: PropTypes.string,
    showOverlay: PropTypes.bool,
    onDismiss: PropTypes.func,
};
